import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, X } from "lucide-react";

/**
 * Editable citations list with per-row verification controls.
 * Verification only works on saved drafts (requires an entry id).
 * The parent owns the citations array; this component is purely controlled.
 */
export default function CitationsEditor({
  citations,
  entryId,
  onAdd,
  onUpdate,
  onRemove,
  onVerify,
  onUnverify,
}) {
  return (
    <div className="border-t border-black/10 pt-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="overline">/citations · receipts</div>
          <div className="text-xs text-[#555] mt-1">
            Each citation must be marked verified by an editor before this entry can be published.
          </div>
        </div>
        <Button type="button" onClick={onAdd} variant="outline" className="rounded-none border-black h-9" data-testid="add-citation-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add citation
        </Button>
      </div>

      <div className="space-y-3">
        {citations.map((c, i) => (
          <div
            key={c._cid || `${c.url || "nourl"}-${i}`}
            className={`border ${c.verified ? "border-[#008A00] bg-[#F1F8F1]" : "border-black/15 bg-[#FAFAFA]"} p-3`}
            data-testid={`citation-row-${i}`}
          >
            <div className="flex justify-between mb-2 items-center flex-wrap gap-2">
              <span className="overline">№ 0{i + 1}</span>
              <div className="flex items-center gap-3">
                {entryId ? (
                  <label className="inline-flex items-center gap-2 text-xs font-mono-plex uppercase tracking-wider cursor-pointer">
                    <Checkbox
                      checked={!!c.verified}
                      onCheckedChange={(v) => v ? onVerify(i) : onUnverify(i)}
                      className="rounded-none border-black data-[state=checked]:bg-[#008A00] data-[state=checked]:border-[#008A00]"
                      data-testid={`verify-checkbox-${i}`}
                    />
                    <span>{c.verified ? "Verified" : "Mark verified"}</span>
                  </label>
                ) : (
                  <span className="text-xs text-[#888] font-mono-plex uppercase tracking-wider">
                    Save draft to enable verification
                  </span>
                )}
                <button type="button" onClick={() => onRemove(i)} className="text-[#D32F2F]" data-testid={`rm-citation-${i}`}>
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {c.verified && c.verified_by && (
              <div className="font-mono-plex text-[11px] uppercase tracking-[0.2em] text-[#008A00] mb-2">
                ✓ Verified by {c.verified_by}{c.verified_at ? ` · ${c.verified_at.slice(0, 10)}` : ""}
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-2">
              <Input placeholder="Title" value={c.title || ""} onChange={(e) => onUpdate(i, "title", e.target.value)} className="rounded-none border-black/30 h-10" />
              <Input placeholder="Publisher" value={c.publisher || ""} onChange={(e) => onUpdate(i, "publisher", e.target.value)} className="rounded-none border-black/30 h-10" />
              <Input placeholder="URL" value={c.url || ""} onChange={(e) => onUpdate(i, "url", e.target.value)} className="rounded-none border-black/30 h-10 md:col-span-2" />
              <Input placeholder="Date (YYYY-MM-DD)" value={c.date || ""} onChange={(e) => onUpdate(i, "date", e.target.value)} className="rounded-none border-black/30 h-10" />
              <Input placeholder="Quote (optional)" value={c.quote || ""} onChange={(e) => onUpdate(i, "quote", e.target.value)} className="rounded-none border-black/30 h-10" />
            </div>
          </div>
        ))}
        {citations.length === 0 && (
          <div className="text-sm text-[#555] border border-dashed border-black/20 p-4 text-center">
            No citations yet. Add at least one source — entries cannot be published without one.
          </div>
        )}
      </div>
    </div>
  );
}
